import { Fragment, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
  faFile,
  faFolder,
  faFolderOpen,
  faPen,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import type { PlaygroundTreeNode } from "./playground-file-tree";
import {
  buildPlaygroundFileTree,
  parentFolderPath,
  playgroundFileIcon,
} from "./playground-file-tree";

/** Where a not-yet-named file/folder is about to be created. */
export interface PendingNewItem {
  readonly kind: "file" | "folder";
  readonly parent: string | null;
}

export interface PlaygroundFileExplorerProps {
  readonly files: readonly { path: string; content: string }[];
  readonly activePath: string;
  readonly targetFolder: string | null;
  readonly readOnly: boolean;
  readonly pendingNew: PendingNewItem | null;
  readonly onOpenFile: (path: string) => void;
  readonly onSelectFolder: (path: string | null) => void;
  readonly onRenameFile: (path: string) => void;
  readonly onDeleteFile: (path: string) => void;
  readonly onRenameFolder: (path: string) => void;
  readonly onDeleteFolder: (path: string) => void;
  readonly onMoveFile: (path: string, destFolder: string | null) => void;
  readonly onMoveFolder: (folderPath: string, destFolder: string | null) => void;
  readonly onCommitNew: (name: string) => void;
  readonly onCancelNew: () => void;
}

/** Sentinel for "the project root" as a drop target (real folder paths are never empty). */
const ROOT_DROP_KEY = "\u0000root";

const DRAG_MIME = "application/x-playground-item";

function indentRem(depth: number): string {
  return `${0.4 + depth * 0.85}rem`;
}

export function PlaygroundFileExplorer(props: PlaygroundFileExplorerProps) {
  const {
    files,
    activePath,
    targetFolder,
    readOnly,
    pendingNew,
    onOpenFile,
    onSelectFolder,
    onRenameFile,
    onDeleteFile,
    onRenameFolder,
    onDeleteFolder,
    onMoveFile,
    onMoveFolder,
    onCommitNew,
    onCancelNew,
  } = props;

  const tree = buildPlaygroundFileTree(files);
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(() => new Set());
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Auto-expand ancestors of the active file, the target folder, and any pending new item.
  useEffect(() => {
    setCollapsed((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set(prev);
      const open = (p: string | null) => {
        let cur = p;
        while (cur) {
          next.delete(cur);
          cur = parentFolderPath(cur);
        }
      };
      open(parentFolderPath(activePath));
      open(targetFolder);
      if (pendingNew) open(pendingNew.parent);
      return next.size === prev.size ? prev : next;
    });
  }, [activePath, targetFolder, pendingNew]);

  // Reset the inline draft whenever a fresh new-item prompt appears.
  useEffect(() => {
    setDraft("");
    if (pendingNew) {
      // Focus on next tick once the input has mounted.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [pendingNew]);

  const toggleCollapse = (path: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const handleDragStart = (
    e: React.DragEvent,
    kind: "file" | "folder",
    path: string
  ) => {
    if (readOnly || (kind === "file" && path === "main.py")) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.setData(DRAG_MIME, JSON.stringify({ kind, path }));
    e.dataTransfer.effectAllowed = "move";
  };

  const allowDrop = (e: React.DragEvent, key: string) => {
    if (readOnly) return;
    if (!e.dataTransfer.types.includes(DRAG_MIME)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDropTarget(key);
  };

  const handleDrop = (e: React.DragEvent, destFolder: string | null) => {
    e.preventDefault();
    e.stopPropagation();
    setDropTarget(null);
    if (readOnly) return;
    const raw = e.dataTransfer.getData(DRAG_MIME);
    if (!raw) return;
    let item: { kind: "file" | "folder"; path: string };
    try {
      item = JSON.parse(raw);
    } catch {
      return;
    }
    if (item.kind === "file") onMoveFile(item.path, destFolder);
    else onMoveFolder(item.path, destFolder);
  };

  const renderActions = (
    kind: "file" | "folder",
    path: string,
    onRename: (p: string) => void,
    onDelete: (p: string) => void
  ) => {
    if (readOnly) return null;
    const canDelete = !(kind === "file" && path === "main.py");
    return (
      <span className="playground-file-actions">
        <button
          type="button"
          className="playground-file-action"
          onClick={(e) => {
            e.stopPropagation();
            onRename(path);
          }}
          title={`Rename ${path}`}
          aria-label={`Rename ${path}`}
        >
          <FontAwesomeIcon icon={faPen} fixedWidth />
        </button>
        {canDelete ? (
          <button
            type="button"
            className="playground-file-action playground-file-action--danger"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(path);
            }}
            title={`Delete ${path}`}
            aria-label={`Delete ${path}`}
          >
            <FontAwesomeIcon icon={faTrash} fixedWidth />
          </button>
        ) : null}
      </span>
    );
  };

  const renderNewInput = (depth: number) => {
    if (!pendingNew) return null;
    return (
      <li
        className="playground-file-row playground-file-new-row"
        style={{ paddingLeft: indentRem(depth) }}
      >
        <span className="playground-file-chevron-spacer" aria-hidden />
        <FontAwesomeIcon
          icon={pendingNew.kind === "folder" ? faFolder : faFile}
          className="playground-file-icon"
          fixedWidth
        />
        <input
          ref={inputRef}
          type="text"
          className="playground-file-new-input"
          value={draft}
          placeholder={pendingNew.kind === "folder" ? "folder name" : "file name (adds .py)"}
          aria-label={pendingNew.kind === "folder" ? "New folder name" : "New file name"}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onCommitNew(draft);
            } else if (e.key === "Escape") {
              e.preventDefault();
              onCancelNew();
            }
          }}
          onBlur={() => {
            if (draft.trim()) onCommitNew(draft);
            else onCancelNew();
          }}
        />
      </li>
    );
  };

  const renderNodes = (nodes: readonly PlaygroundTreeNode[], depth: number) =>
    nodes.map((node) =>
      node.kind === "folder" ? renderFolder(node, depth) : renderFile(node, depth)
    );

  const renderFolder = (
    node: Extract<PlaygroundTreeNode, { kind: "folder" }>,
    depth: number
  ) => {
    const isCollapsed = collapsed.has(node.path);
    const selected = targetFolder === node.path;
    const isDrop = dropTarget === node.path;
    return (
      <Fragment key={`folder:${node.path}`}>
        <li
          className={`playground-file-row playground-file-row--folder${selected ? " is-target-folder" : ""}${isDrop ? " is-drop-target" : ""}`}
          style={{ paddingLeft: indentRem(depth) }}
          draggable={!readOnly}
          onDragStart={(e) => handleDragStart(e, "folder", node.path)}
          onDragOver={(e) => allowDrop(e, node.path)}
          onDragLeave={() => setDropTarget((cur) => (cur === node.path ? null : cur))}
          onDrop={(e) => handleDrop(e, node.path)}
        >
          <button
            type="button"
            className="playground-file-open playground-file-open--folder"
            onClick={() => {
              onSelectFolder(node.path);
              toggleCollapse(node.path);
            }}
            title={`Folder: ${node.path}`}
          >
            <FontAwesomeIcon
              icon={isCollapsed ? faChevronRight : faChevronDown}
              className="playground-file-chevron"
              fixedWidth
            />
            <FontAwesomeIcon
              icon={isCollapsed ? faFolder : faFolderOpen}
              className="playground-file-icon playground-file-icon--folder"
              fixedWidth
            />
            <span className="playground-file-label">{node.name}</span>
          </button>
          {renderActions("folder", node.path, onRenameFolder, onDeleteFolder)}
        </li>
        {!isCollapsed ? (
          <>
            {renderNodes(node.children, depth + 1)}
            {pendingNew && pendingNew.parent === node.path ? renderNewInput(depth + 1) : null}
          </>
        ) : null}
      </Fragment>
    );
  };

  const renderFile = (
    node: Extract<PlaygroundTreeNode, { kind: "file" }>,
    depth: number
  ) => {
    const isActive = node.path === activePath;
    const draggable = !readOnly && node.path !== "main.py";
    return (
      <li
        key={`file:${node.path}`}
        className={`playground-file-row${isActive ? " is-active" : ""}`}
        style={{ paddingLeft: indentRem(depth) }}
        draggable={draggable}
        onDragStart={(e) => handleDragStart(e, "file", node.path)}
      >
        <button
          type="button"
          className="playground-file-open"
          onClick={() => {
            onSelectFolder(node.path.includes("/") ? node.path.slice(0, node.path.lastIndexOf("/")) : null);
            onOpenFile(node.path);
          }}
          title={node.path}
        >
          <span className="playground-file-chevron-spacer" aria-hidden />
          <FontAwesomeIcon icon={playgroundFileIcon(node.path)} className="playground-file-icon" fixedWidth />
          <span className="playground-file-label">{node.name}</span>
        </button>
        {renderActions("file", node.path, onRenameFile, onDeleteFile)}
      </li>
    );
  };

  return (
    <ul
      className={`playground-file-list${dropTarget === ROOT_DROP_KEY ? " is-root-drop-target" : ""}`}
      onDragOver={(e) => allowDrop(e, ROOT_DROP_KEY)}
      onDragLeave={() => setDropTarget((cur) => (cur === ROOT_DROP_KEY ? null : cur))}
      onDrop={(e) => handleDrop(e, null)}
    >
      <li
        className={`playground-file-row playground-file-row--folder playground-file-row--root${targetFolder === null ? " is-target-folder" : ""}`}
        style={{ paddingLeft: "0.4rem" }}
      >
        <button
          type="button"
          className="playground-file-open playground-file-open--folder"
          onClick={() => onSelectFolder(null)}
          title="Project root"
        >
          <FontAwesomeIcon
            icon={targetFolder === null ? faFolderOpen : faFolder}
            className="playground-file-icon playground-file-icon--folder"
            fixedWidth
          />
          <span className="playground-file-label">project</span>
        </button>
      </li>
      {renderNodes(tree, 1)}
      {pendingNew && pendingNew.parent === null ? renderNewInput(1) : null}
    </ul>
  );
}
